import { useEffect, useState } from 'react'

import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { KeyMessageBannerProps } from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import KeyMessageBanner, {
  ImageAlignmentVariants,
} from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import type { PageHeaderProps } from '~/components/atoms/PageHeader/PageHeader'
import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import SlideOutPanel from '~/components/atoms/SlideOutPanel/SlideOutPanel'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { AnimatedMessageBannerProps } from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import AnimatedMessageBanner from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import type { formatsType } from '~/components/blocks/ArtworksList/ArtworksList'
import ArtworksList from '~/components/blocks/ArtworksList/ArtworksList'
import Catalogue from '~/components/blocks/Catalogue/Catalogue'
import GenreSlider from '~/components/blocks/GenreSlider/GenreSlider'
import InPageNav from '~/components/blocks/InPageNav/InPageNav'
import type { NumberedSliderProps } from '~/components/blocks/NumberedSlider/NumberedSlider'
import NumberedSlider from '~/components/blocks/NumberedSlider/NumberedSlider'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import type { TextWithAccordionProps } from '~/components/blocks/TextWithAccordion/TextWithAccordion'
import TextWithAccordion from '~/components/blocks/TextWithAccordion/TextWithAccordion'
import { ComponentTheme } from '~/components/themes'
import {
  factList,
  format,
  getDocumentDescription,
  internationalizedField,
  internationalizedTextField,
  link,
  myPortfolioBanner,
  notification,
  overviewBanner,
} from '~/schemas/common'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useSanity } from '~/services/sanity'
import type { ProcessedSettings } from '~/services/sanity/siteSettings'
import { metaQuery } from '~/utils/seo'
import sortObjectsByString from '~/utils/sortObjectsByString'
import uniqueObjects from '~/utils/uniqueObjects'
import { slugFromContext, urlQuery } from '~/utils/urlResolver'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes
import {
  buildInverseTranslationQuery,
  processInverseQueryResults,
} from '~/utils/articles/translationHelpers'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

export interface PageData {
  genreId: string
  slug: string
  pageHeaderData: PageHeaderProps
  documentDescription: {
    title?: string
    summary?: string
    blocks?: TextWithAccordionProps['data']
  }
  banner: KeyMessageBannerProps
  genreArtists: ProcessedSettings['featuredArtists']
  allGenreArtists: ProcessedSettings['featuredArtists']
  factList: NumberedSliderProps
  formats: formatsType
  breadcrumb: BreadcrumbProps['list']
  relatedArticles: ArticlePreviewCardProps[]
  otherGenres: ProcessedSettings['genres']
  preview: boolean
  queryParams: any
  overviewBanner?: AnimatedMessageBannerProps
}

const getQuery = (
  limiter: any,
  scorer: any = '',
  _id?: string,
  lang = 'en'
) => {
  const id = _id ? `'${_id}'` : '^._id'
  return `*[_type == 'genre' && (slug.current == $slug || $slug in _pastSlugs) ${limiter}] ${scorer}[0] {
    "slug": slug.current,
    ${urlQuery},
    ${metaQuery('genre')},
    ${getDocumentDescription(lang)}
    "genreId": _id,
    "pageHeaderData": {
      "title": ${internationalizedField('name')},
      "summary": ${internationalizedTextField('summary')},
    },
    "genreArtists": *[_type == 'artist' && references(${id}) ${limiter}] | order(coalesce(totalArtworks, -1) desc)[0...7]{
      _id,
      name,
      image,
      "slug": slug.current,
      totalArtworks,
    },
    "allGenreArtists": *[_type == 'artist' && ${id} in genres[]._ref ${limiter}]{ "name": ${internationalizedField('name')}, image, "slug": slug.current, totalArtworks },
    "banner": *[_type == 'banner' && showOnHomepage != true && references(${id})] | order(_createdAt desc)[0]{
      title,
      "subtitle": body,
      "images": {
        image
      },
      "textMask": applyOverlay,
      ${link}
    },
    ${notification},
    ${factList(id, lang)},
    "formats": *[_type == 'artwork' && genre._ref == ${id} && defined(formatDescription) ${limiter}]{
      ${format}
    },
    ${buildInverseTranslationQuery(
      `parent._ref == ${id}`,
      lang,
      'relatedArticles',
      'coalesce(publishDate, _createdAt) desc',
      3
    )},
    ${myPortfolioBanner}
    "otherGenres": *[_type == 'genre' && slug.current != $slug ${limiter}]{
      "name": ${internationalizedField('name')},
      "slug": slug.current,
      _id,
      image
    },
    ${overviewBanner}
  }`
}

const artworksListEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDsAfterSearch,
    name: SearchAnalystEventNames.genreArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.genreArtworkArtistClicked,
  },
  filter: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.genreArtworkFilterClicked,
  },
}

const Genre: NextPage<PageData> = (props: PageData) => {
  const t = useTranslations('common')
  const tGenre = useTranslations('genrePage')

  const resetId = (id: string) => {
    return id?.replace('drafts.', '')
  }

  const {
    slug,
    pageHeaderData,
    documentDescription,
    banner,
    genreArtists,
    allGenreArtists,
    factList,
    formats,
    relatedArticles,
    otherGenres,
    overviewBanner,
  } = props

  const { breadcrumb, preview } = props
  const genreId = resetId(props.genreId)

  const [sidePanelShowing, setSidePanelShowing] = useState<boolean>(false)

  const toggleSideBar = () => {
    setSidePanelShowing(!sidePanelShowing)
  }

  // Building up the jump links
  const jumpToLinks = [
    {
      title: t('buy'),
      slug: 'buy',
    },
  ]

  if (documentDescription.summary)
    jumpToLinks.push({
      title: tGenre('movementOverview'),
      slug: 'movement-overview',
    })
  if (factList) jumpToLinks.push({ title: t('tenFacts'), slug: '10-facts' })

  if (relatedArticles.length > 0)
    jumpToLinks.push({ title: t('articles'), slug: 'articles' })

  const { sendSearchInsightsViewEvent } = useSearchAnalyst()

  useEffect(() => {
    sendSearchInsightsViewEvent(SearchAnalystMethods.viewedFilters, {
      eventName: SearchAnalystEventNames.genrePageViewed,
      indexType: EsIndexTypes.ARTWORKS,
      filters: [`genres._id:${genreId}`],
    })
  }, [])

  return (
    <>
      <Breadcrumb list={breadcrumb} />
      <PageHeader {...pageHeaderData} />
      <InPageNav items={jumpToLinks} title={pageHeaderData.title} />
      <VerticalPadder noPaddingBottom={true}>
        <ArtistProfileList
          artists={genreArtists}
          totalArtists={allGenreArtists.length}
          allBtnHandler={toggleSideBar}
          searchInsightsEvent={{
            method: SearchAnalystMethods.clickedFilters,
            name: SearchAnalystEventNames.genreArtistClicked,
          }}
          showViewAll={true}
        />
      </VerticalPadder>

      <div id="buy-container">
        <ArtworksList
          sectionId="buy"
          queryId={genreId}
          pageSize={12}
          type="genre"
          formats={formats}
          searchPlaceholder={t('searchArtistOrArtworkTitle')}
          searchInsightsEvents={artworksListEvents}
          hideTradingFloor
          hideFilters={['format', 'artist']}
          slugParams={true}
        />
      </div>

      {documentDescription.blocks && documentDescription.summary && (
        <div id="movement-overview-container">
          <TextWithAccordion
            title={
              documentDescription.title
                ? documentDescription.title
                : tGenre('movementOverview')
            }
            data={documentDescription.blocks}
            summary={documentDescription.summary}
            sectionId="movement-overview"
          />
        </div>
      )}

      {factList && (
        <NumberedSlider
          {...factList}
          elementId="10-facts"
          linkText={t('allFacts', {
            title:
              typeof pageHeaderData.title === 'string'
                ? pageHeaderData.title
                : '',
          })}
          seeMoreButtonText={t('allFacts', {
            title:
              typeof pageHeaderData.title === 'string'
                ? pageHeaderData.title
                : '',
          })}
        />
      )}

      {relatedArticles.length > 0 && (
        <div id="articles-container">
          <RelatedArticles
            articles={relatedArticles}
            theme={factList ? ComponentTheme.secondary : ComponentTheme.primary}
            sectionId="articles"
          />
        </div>
      )}

      <Fixture
        theme={
          relatedArticles && factList
            ? ComponentTheme.primary
            : ComponentTheme.secondary
        }
        verticalPadding={true}
      >
        <GenreSlider
          genres={otherGenres}
          title={tGenre('otherGenres')}
          searchInsightsEvent={{
            method: SearchAnalystMethods.clickedFilters,
            name: SearchAnalystEventNames.genreOtherGenreClicked,
          }}
        />
      </Fixture>

      {banner && banner?.images && (
        <KeyMessageBanner
          {...banner}
          imageAlign={ImageAlignmentVariants.full}
        />
      )}

      {overviewBanner && <AnimatedMessageBanner {...overviewBanner} />}

      <SlideOutPanel isShowing={sidePanelShowing} closeHandler={toggleSideBar}>
        <Catalogue
          items={allGenreArtists}
          recordType={RecordTypes.ARTIST}
          orderBySurname
        />
      </SlideOutPanel>
    </>
  )
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticProps(context) {
    try {
      const slug = slugFromContext(context)
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { client, limiter, scorer } = await useSanity(context)
      const lang = (
        Array.isArray(context?.params?.lang)
          ? context.params.lang[0]
          : context?.params?.lang || 'en'
      ) as Locale
      const messages = await loadMessages(lang)

      // Get page number from route params for path-based pagination
      const pageNumber = context?.params?.page ? Number(context.params.page) : 1
      const nextPageNumber = pageNumber + 1
      const data = await client.fetch(
        getQuery(limiter, scorer, undefined, lang),
        {
          slug: slugFromContext(context),
          lang: lang,
        }
      )
      const faqSchemaBlocks = data?.documentDescription?.blocks.filter(
        (item: any) => item.faqSchema !== false
      )
      const faqSchemaSummary = data?.documentDescription?.blocks.filter(
        (item: any) => item.faqSchema !== false
      )

      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        [slug],
        '',
        undefined,
        messages,
        lang
      )

      const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

      if (data === null) {
        return {
          notFound: true,
        }
      }

      // Post-process articles for translation extraction
      if (data.relatedArticles) {
        data.relatedArticles = processInverseQueryResults(
          data.relatedArticles,
          lang
        )
      }

      return {
        props: {
          notFound: Object.keys(data).length < 1,
          ...data,
          _type: 'genre',
          smallHeader: true,
          formats: sortObjectsByString(uniqueObjects(data.formats), 'name'),
          genreArtists: uniqueObjects(data.genreArtists, 'name'),
          allGenreArtists: uniqueObjects(data.allGenreArtists, 'name'),
          queryParams: {
            slug: slugFromContext(context),
            nextPage: nextPageNumber || 0,
          },
          preview: !!context.preview,
          breadcrumb,
          breadcrumbJsonLd,
          jsonLd: {
            faqJsonLd: [...faqSchemaBlocks, ...faqSchemaSummary],
          },
        },
      }
    } catch {
      return {
        notFound: true,
      }
    }
  }

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)

  if ('props' in result) {
    return {
      props: await addTranslations(result.props, context),
    }
  }

  return result
})

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default Genre
